import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Layout,
  Menu,
  Input,
  Avatar,
  Dropdown,
  Button,
  Space,
  Drawer,
  Typography,
  Divider,
  Badge,
} from 'antd';
import {
  HomeOutlined,
  AppstoreOutlined,
  SearchOutlined,
  UserOutlined,
  EditOutlined,
  SettingOutlined,
  LogoutOutlined,
  LoginOutlined,
  UserAddOutlined,
  MenuOutlined,
  GlobalOutlined,
  DownOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contexts/AuthContext';
import { getCategories, recordPageView, getMyStats, getUnreadCount } from '../../api';
import './style.css';

const { Header, Content, Footer } = Layout;
const { Text } = Typography;

const AppLayout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const pageViewRecorded = useRef(false);

  useEffect(() => {
    getCategories().then((res) => {
      if (res.data) setCategories(res.data);
    }).catch(() => {});

    if (!pageViewRecorded.current) {
      pageViewRecorded.current = true;
      recordPageView().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (user) {
      getMyStats().then((res) => {
        if (res.data) setStats(res.data);
      }).catch(() => {});
    } else {
      setStats({});
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      getUnreadCount().then((res) => {
        if (res.data) setUnreadCount(res.data.count || 0);
      }).catch(() => {});

      const timer = setInterval(() => {
        getUnreadCount().then((res) => {
          if (res.data) setUnreadCount(res.data.count || 0);
        }).catch(() => {});
      }, 30000);

      return () => clearInterval(timer);
    } else {
      setUnreadCount(0);
    }
  }, [user]);

  const currentLang = i18n.language?.startsWith('zh') ? 'zh' : 'en';

  const toggleLanguage = () => {
    i18n.changeLanguage(currentLang === 'zh' ? 'en' : 'zh');
  };

  const handleSearch = (value) => {
    if (value.trim()) {
      navigate(`/search?keyword=${encodeURIComponent(value.trim())}`);
      setSearchValue('');
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const categoryMenuItems = categories.map((cat) => ({
    key: `/category/${cat.id}`,
    label: cat.name,
  }));

  const navItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: t('nav.home', 'Home'),
    },
    {
      key: 'categories',
      icon: <AppstoreOutlined />,
      label: t('nav.categories', 'Categories'),
      children: categoryMenuItems.length > 0
        ? categoryMenuItems
        : [{ key: 'no-cat', label: t('nav.noCategories', 'No categories'), disabled: true }],
    },
  ];

  const userMenuItems = user
    ? [
        {
          key: 'profile',
          icon: <UserOutlined />,
          label: t('nav.profile', 'Profile'),
        },
        ...(user.role === 'ADMIN'
          ? [{
              key: 'admin',
              icon: <SettingOutlined />,
              label: (
                <Badge count={unreadCount} size="small" offset={[8, 0]}>
                  {t('nav.admin', 'Admin')}
                </Badge>
              ),
            }]
          : []),
        { type: 'divider' },
        {
          key: 'logout',
          icon: <LogoutOutlined />,
          label: t('nav.logout', 'Logout'),
          danger: true,
        },
      ]
    : [
        {
          key: 'login',
          icon: <LoginOutlined />,
          label: t('nav.login', 'Login'),
        },
        {
          key: 'register',
          icon: <UserAddOutlined />,
          label: t('nav.register', 'Register'),
        },
      ];

  const handleUserMenuClick = ({ key }) => {
    const routes = {
      profile: `/profile/${user?.id}`,
      'new-article': '/editor',
      admin: '/admin',
      login: '/login',
      register: '/register',
    };
    if (key === 'logout') {
      handleLogout();
    } else if (routes[key]) {
      navigate(routes[key]);
    }
  };

  const handleNavClick = ({ key }) => {
    if (key !== 'no-cat') {
      navigate(key);
      setMobileMenuOpen(false);
    }
  };

  const activeKey = location.pathname === '/' ? '/' : undefined;

  return (
    <Layout className="app-layout">
      <Header className="app-header">
        <div className="header-inner">
          <Link to="/" className="logo">
            <span className="logo-icon">✍️</span>
            <span className="logo-text">随心享笔</span>
          </Link>

          <div className="nav-desktop">
            <Menu
              mode="horizontal"
              selectedKeys={[activeKey]}
              items={navItems}
              onClick={handleNavClick}
              className="nav-menu"
            />
          </div>

          <div className="header-actions">
            <Input.Search
              placeholder={t('nav.search', 'Search articles...')}
              allowClear
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              className="header-search"
              prefix={<SearchOutlined />}
            />

            {user && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate('/editor')}
                className="write-article-btn"
              >
                {t('nav.newArticle', 'New Article')}
              </Button>
            )}

            <Button
              type="text"
              className="lang-switch"
              icon={<GlobalOutlined />}
              onClick={toggleLanguage}
            >
              {currentLang === 'zh' ? 'EN' : '中'}
            </Button>

            {user?.role === 'ADMIN' && (
              <Badge count={unreadCount} size="small">
                <Button
                  type="text"
                  icon={<BellOutlined style={{ fontSize: 18 }} />}
                  onClick={() => navigate('/admin')}
                  className="header-bell"
                />
              </Badge>
            )}

            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
            >
              {user ? (
                <Space className="user-trigger" role="button">
                  <Avatar
                    size="small"
                    src={user.avatar}
                    icon={!user.avatar && <UserOutlined />}
                  />
                  <span className="user-name">{user.nickname || user.username}</span>
                  <DownOutlined style={{ fontSize: 10 }} />
                </Space>
              ) : (
                <Button type="primary" ghost icon={<UserOutlined />}>
                  {t('nav.login', 'Login')}
                </Button>
              )}
            </Dropdown>

            <Button
              type="text"
              className="mobile-menu-btn"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuOpen(true)}
            />
          </div>
        </div>
      </Header>

      <Drawer
        title={
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="logo">
            <span className="logo-icon">✍️</span>
            <span className="logo-text">随心享笔</span>
          </Link>
        }
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={280}
        className="mobile-drawer"
      >
        <Input.Search
          placeholder={t('nav.search', 'Search articles...')}
          allowClear
          onSearch={handleSearch}
          style={{ marginBottom: 16 }}
        />
        {user && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            block
            style={{ marginBottom: 16 }}
            onClick={() => { navigate('/editor'); setMobileMenuOpen(false); }}
          >
            {t('nav.newArticle', 'New Article')}
          </Button>
        )}
        <Menu
          mode="inline"
          selectedKeys={[activeKey]}
          items={navItems}
          onClick={handleNavClick}
        />
        <Divider />
        <Menu
          mode="inline"
          items={userMenuItems}
          onClick={handleUserMenuClick}
        />
        <Divider />
        <Button block icon={<GlobalOutlined />} onClick={toggleLanguage}>
          {currentLang === 'zh' ? 'Switch to English' : '切换到中文'}
        </Button>
      </Drawer>

      <Content className="app-content">
        <Outlet />
      </Content>

      <Footer className="app-footer">
        <div className="footer-inner">
          {user && (
            <div className="footer-stats">
              <Space split={<Divider type="vertical" />} wrap>
                <Text type="secondary">
                  {t('footer.myArticles', 'My Articles')}: {stats.myArticles || 0}
                </Text>
                <Text type="secondary">
                  {t('footer.myLikes', 'Likes')}: {stats.myLikes || 0}
                </Text>
                <Text type="secondary">
                  {t('footer.myComments', 'Comments')}: {stats.myComments || 0}
                </Text>
                <Text type="secondary">
                  {t('footer.myViews', 'Views')}: {stats.myViews || 0}
                </Text>
              </Space>
            </div>
          )}
          <div className="footer-info">
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-icp-link"
            >
              备案号：{t('footer.icp', '湘ICP备2026006418号-1')}
            </a>
          </div>
          <div className="footer-copyright">
            <Text type="secondary">© {new Date().getFullYear()} 随心享笔 版权所有</Text>
          </div>
        </div>
      </Footer>
    </Layout>
  );
};

export default AppLayout;
